/**
 * Roya motion primitives — shared custom elements used site-wide.
 * See design.md § Motion System.
 *
 *   <roya-reveal>          staggered scroll-in for its children
 *   <roya-reveal self>     reveals the element itself
 *   <roya-scroll-progress> red page-read progress bar
 *   <roya-to-top>          back-to-top button that fades in past one viewport
 *
 * And the LookBook's scroll language, for any section (attributes):
 *
 *   data-roya-progress     --p (0 → 1) once a frame while on screen. "view"
 *                          (default): how far it has travelled through the
 *                          viewport; "exit": how far it has scrolled out of
 *                          the top (a hero that starts on screen).
 *   data-roya-split        a heading whose words rise into place once in view
 *   data-roya-in           gets .is-in once in view (CSS entrances)
 *   data-roya-count        counts its number up once in view, from
 *                          data-roya-count-from (0) after data-roya-count-delay
 *   data-roya-pointer      --mx / --my (-1 → 1): where the mouse is over it, eased
 *   data-roya-magnet       leans toward the mouse and springs back
 *   data-roya-marquee      a CSS marquee that speeds up with the scroll and
 *                          runs the way the page is going
 *
 * All of them no-op under prefers-reduced-motion; the scroll language also
 * keys its CSS off html.roya-motion (set in layout/theme.liquid).
 */

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ */

class RoyaReveal extends HTMLElement {
  connectedCallback() {
    const self = this.hasAttribute('self');
    this.classList.add(self ? 'roya-reveal--self' : 'roya-reveal');

    if (prefersReducedMotion()) {
      this.classList.add('is-inview');
      return;
    }

    if (!self) {
      // Cap the stagger so long grids don't leave the last item waiting.
      const max = parseInt(this.getAttribute('max-stagger'), 10) || 8;
      Array.from(this.children).forEach((child, i) => {
        child.style.setProperty('--reveal-i', Math.min(i, max));
      });
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.classList.add('is-inview');
          this.observer.disconnect();
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('roya-reveal', RoyaReveal);

/* ------------------------------------------------------------------ */

class RoyaScrollProgress extends HTMLElement {
  connectedCallback() {
    if (prefersReducedMotion()) return;

    this.bar = document.createElement('span');
    this.bar.className = 'roya-scroll-progress__bar';
    this.appendChild(this.bar);

    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  update() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
    this.bar.style.transform = `scaleX(${ratio})`;
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }
}

customElements.define('roya-scroll-progress', RoyaScrollProgress);

/* ------------------------------------------------------------------ */

class RoyaToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });

    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.classList.toggle('is-visible', window.scrollY > window.innerHeight);
        this.ticking = false;
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
}

customElements.define('roya-to-top', RoyaToTop);


/* ------------------------------------------------------------------ */
/* The LookBook's scroll language (see the header comment).            */

(() => {
  if (window.__royaScroll) return;
  window.__royaScroll = true;
  if (prefersReducedMotion()) {
    document.documentElement.classList.remove('roya-motion');
    return;
  }
  document.documentElement.classList.add('roya-motion');

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  let viewportHeight = window.innerHeight;
  let frame = 0;

  /* ---------- Scroll progress ---------- */

  const tracked = new Set();

  const progressOf = (el, rect) =>
    el.dataset.royaProgress === 'exit'
      ? clamp(-rect.top / (rect.height || 1))
      : clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));

  const writeProgress = (el, rect) => {
    const p = Math.round(progressOf(el, rect) * 10000) / 10000;
    if (el.__royaP === p) return;
    el.__royaP = p;
    el.style.setProperty('--p', p);
  };

  // Only what is on (or nearly on) screen is measured each frame; whatever
  // leaves is settled at the end it left from.
  const progressObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) tracked.add(entry.target);
        else {
          tracked.delete(entry.target);
          writeProgress(entry.target, entry.boundingClientRect);
        }
      });
      request();
    },
    { rootMargin: '25% 0px 25% 0px' }
  );

  /* ---------- Marquees: speed and direction follow the scroll ---------- */

  const marquees = new Set();
  let lastY = window.scrollY;
  let lastTime = performance.now();
  let velocity = 0; // px per ms, smoothed
  let direction = 1;
  let rate = 1;

  const marqueeObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) marquees.add(entry.target);
      else marquees.delete(entry.target);
    });
  });

  function animationsOf(track) {
    if (!track.__royaAnimations) {
      track.__royaAnimations = track.getAnimations().filter((animation) => animation instanceof CSSAnimation);
      // Give a reversed run room to play: a backwards animation stops at 0.
      track.__royaAnimations.forEach((animation) => {
        const duration = animation.effect.getTiming().duration;
        if (typeof duration === 'number' && duration > 0) {
          animation.currentTime = duration * 1000 + ((animation.currentTime || 0) % duration);
        }
      });
    }
    return track.__royaAnimations;
  }

  function updateMarquees() {
    velocity *= 0.9;
    const target = direction * (1 + Math.min(Math.abs(velocity) * 2.4, 5));
    rate += (target - rate) * 0.12;
    if (Math.abs(target - rate) < 0.01) rate = target;
    marquees.forEach((track) => {
      animationsOf(track).forEach((animation) => {
        if (Math.abs((animation.__royaRate || 1) - rate) < 0.02) return;
        animation.__royaRate = rate;
        if (animation.updatePlaybackRate) animation.updatePlaybackRate(rate);
        else animation.playbackRate = rate;
      });
    });
    return Math.abs(target - rate) > 0.01 || Math.abs(velocity) > 0.01;
  }

  /* ---------- The frame loop ---------- */

  function tick() {
    frame = 0;
    tracked.forEach((el) => writeProgress(el, el.getBoundingClientRect()));
    if (marquees.size && updateMarquees()) request();
  }

  function request() {
    if (!frame) frame = requestAnimationFrame(tick);
  }

  window.addEventListener(
    'scroll',
    () => {
      const now = performance.now();
      const y = window.scrollY;
      const moved = (y - lastY) / Math.max(1, now - lastTime);
      velocity = velocity * 0.6 + moved * 0.4;
      if (Math.abs(moved) > 0.02) direction = moved > 0 ? 1 : -1;
      lastY = y;
      lastTime = now;
      request();
    },
    { passive: true }
  );

  window.addEventListener('resize', () => {
    viewportHeight = window.innerHeight;
    request();
  });

  /* ---------- Words that rise into place ---------- */

  function split(el) {
    if (el.classList.contains('roya-split')) return;
    const label = el.textContent.replace(/\s+/g, ' ').trim();
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.tagName !== 'BR') walk(child);
          return;
        }
        if (child.nodeType !== Node.TEXT_NODE) return;
        const fragment = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (!part.trim()) {
            fragment.append(document.createTextNode(' '));
            return;
          }
          const word = document.createElement('span');
          word.className = 'roya-split__w';
          const inner = document.createElement('span');
          inner.className = 'roya-split__i';
          inner.style.setProperty('--i', index);
          index += 1;
          inner.textContent = part;
          word.append(inner);
          fragment.append(word);
        });
        child.replaceWith(fragment);
      });
    };
    walk(el);
    el.style.setProperty('--words', index);
    // A heading keeps one name for screen readers rather than a word at a time.
    if (/^H[1-6]$/.test(el.tagName) && label) {
      el.setAttribute('aria-label', label);
      el.querySelectorAll('.roya-split__w').forEach((word) => word.setAttribute('aria-hidden', 'true'));
    }
    el.classList.add('roya-split');
  }

  /* ---------- Numbers that count up ---------- */

  function prepareCount(el) {
    const text = el.textContent.trim();
    const match = text.match(/\d+/);
    if (!match) return;
    // Padded to the final width ("000" → "100"), so nothing shifts as it counts.
    const width = match[0].length;
    const format = (n) => text.replace(match[0], String(n).padStart(width, '0'));
    el.__royaCount = {
      text,
      format,
      from: parseInt(el.dataset.royaCountFrom || '0', 10),
      to: parseInt(match[0], 10),
    };
    el.textContent = format(el.__royaCount.from);
  }

  function countUp(el) {
    const count = el.__royaCount;
    if (!count) return;
    const duration = parseFloat(el.dataset.royaCountDuration || '1400');
    const delay = parseFloat(el.dataset.royaCountDelay || '0');
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    setTimeout(() => {
      el.classList.add('is-counting');
      const start = performance.now();
      const step = (now) => {
        const t = clamp((now - start) / duration);
        el.textContent = t < 1 ? count.format(Math.round(count.from + (count.to - count.from) * ease(t))) : count.text;
        if (t < 1) requestAnimationFrame(step);
        else {
          el.classList.remove('is-counting');
          el.classList.add('is-counted');
        }
      };
      requestAnimationFrame(step);
    }, delay);
  }

  /* ---------- In view ---------- */

  const inObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        inObserver.unobserve(entry.target);
        entry.target.classList.add('is-in');
        if (entry.target.hasAttribute('data-roya-count')) countUp(entry.target);
      });
    },
    { threshold: 0.2, rootMargin: '0px 0px -6% 0px' }
  );

  /* ---------- Pointer: --mx / --my, eased ---------- */

  function bindPointer(el) {
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    let raf = 0;
    const step = () => {
      current.x += (target.x - current.x) * 0.12;
      current.y += (target.y - current.y) * 0.12;
      const settled = Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001;
      if (settled) {
        current.x = target.x;
        current.y = target.y;
      }
      el.style.setProperty('--mx', current.x.toFixed(3));
      el.style.setProperty('--my', current.y.toFixed(3));
      raf = settled ? 0 : requestAnimationFrame(step);
    };
    const run = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };
    el.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;
      const rect = el.getBoundingClientRect();
      target.x = clamp((event.clientX - rect.left) / rect.width) * 2 - 1;
      target.y = clamp((event.clientY - rect.top) / rect.height) * 2 - 1;
      el.classList.add('is-pointing');
      run();
    });
    el.addEventListener('pointerleave', () => {
      target.x = 0;
      target.y = 0;
      el.classList.remove('is-pointing');
      run();
    });
  }

  /* ---------- Magnetic buttons ---------- */

  function bindMagnet(el) {
    const strength = parseFloat(el.dataset.royaMagnet) || 0.22;
    const reach = 16; // px, however wide the button
    el.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;
      const rect = el.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left - rect.width / 2) * strength, -reach, reach);
      const y = clamp((event.clientY - rect.top - rect.height / 2) * strength, -reach, reach);
      el.style.setProperty('--magnet-x', `${x.toFixed(1)}px`);
      el.style.setProperty('--magnet-y', `${y.toFixed(1)}px`);
      el.classList.add('is-magnet');
    });
    el.addEventListener('pointerleave', () => {
      el.style.setProperty('--magnet-x', '0px');
      el.style.setProperty('--magnet-y', '0px');
      el.classList.remove('is-magnet');
    });
  }

  /* ---------- Wiring ---------- */

  function scan(scope) {
    scope.querySelectorAll('[data-roya-progress]').forEach((el) => {
      if (el.__royaProgress) return;
      el.__royaProgress = true;
      writeProgress(el, el.getBoundingClientRect());
      progressObserver.observe(el);
    });
    scope.querySelectorAll('[data-roya-split]').forEach((el) => {
      split(el);
      inObserver.observe(el);
    });
    scope.querySelectorAll('[data-roya-count]').forEach((el) => {
      if (el.__royaCount) return;
      prepareCount(el);
      inObserver.observe(el);
    });
    scope.querySelectorAll('[data-roya-in]').forEach((el) => inObserver.observe(el));
    scope.querySelectorAll('[data-roya-pointer]').forEach((el) => {
      if (el.__royaPointer) return;
      el.__royaPointer = true;
      bindPointer(el);
    });
    scope.querySelectorAll('[data-roya-magnet]').forEach((el) => {
      if (el.__royaMagnet) return;
      el.__royaMagnet = true;
      bindMagnet(el);
    });
    scope.querySelectorAll('[data-roya-marquee]').forEach((el) => marqueeObserver.observe(el));
  }

  const start = () => scan(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Sections re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
